import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { RootState, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { useLicenses } from "@dashboard/licenses/api/helpers";
import { useDeviceRegistrationGroup } from "@dashboard/shared/hooks/useDeviceRegistrationGroup";
import { useUpdateWorkspaceMutation } from "@dashboard/workspaces/api";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { ConfirmDialog } from "@devices/components/shared";
import type { Group } from "@devices/types";
import { selectGroupById, selectGroups } from "@groups/store/selectors";
import { Box, DialogContentText, Theme } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { withExpiredWrapper } from "@shared/ExpiredWrapper";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { PaneElement } from "@sidePanel/shared/PaneElement";

import GenerateQRCodeDialogWrapper from "./qrCodeDialog/GenerateQRCodeDialogWrapper";
import { GenerateLicensesSection } from "./GenerateLicensesSection";
import { UniversalLicensesTable } from "./UniversalLicensesTable";

const enrollmentLink = "https://emteria.com/kb/device-enrollment-using-qr-code";

const useStyles = makeStyles()((theme: Theme) => ({
    defaultGroupContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: theme.spacing(2),
    },
    bold: { fontWeight: "bold" },
    contentBox: { display: "flex", justifyContent: "flex-end", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

const EnrollmentTab = (props: Group) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [isCreatingLicense, setIsCreatingLicense] = React.useState(false);
    const [updateWorkspace] = useUpdateWorkspaceMutation();
    const { classes } = useStyles();
    const location = useLocation();
    const { t } = useTranslation(["common", "modals", "sidepanels"]);

    const { data: workspace } = useCurrentWorkspace();
    const { ensureUniversalLicense } = useDeviceRegistrationGroup();

    const groups = useSelector(selectGroups);
    const currentGroup = useSelector((state: RootState) => selectGroupById(state, props.id));
    const isDefaultGroup = props.id === workspace?.defaultGroupId;
    const defaultGroup = useSelector((state: RootState) => (workspace ? selectGroupById(state, workspace.defaultGroupId) : null));

    const licenses = useLicenses();

    const onConfirm = async () => {
        if (workspace) {
            try {
                await updateWorkspace({ ...workspace, defaultGroupId: props.id });
                showSuccessToast(t("sidepanels:groupPanel.toast.defaultGroupChangedSuccess"));
            } finally {
                setOpen(false);
            }
        }
    };

    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const shouldOpenEnrollmentModal = searchParams.get("openEnrollmentModal") === "true";
        if (shouldOpenEnrollmentModal) {
            setIsModalOpen(true);
        }
    }, [location.search]);

    if (!workspace || !currentGroup || currentGroup.id !== props.id) {
        return (
            <PaneElement.Container>
                <PaneElement.Loader />
            </PaneElement.Container>
        );
    }

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:groupPanel.enrollmentTab.zeroTouchProvisioning")}</PaneElement.Header>
                <PaneElement.Text>{t("sidepanels:groupPanel.enrollmentTab.zeroTouchProvisioningDescription")}</PaneElement.Text>

                <Box className={classes.contentBox}>
                    <SecondaryButton onClick={() => window.open(enrollmentLink, "_blank")}>
                        {t("sidepanels:groupPanel.enrollmentTab.moreDetails")}
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={async () => {
                            setIsCreatingLicense(true);
                            try {
                                await ensureUniversalLicense(props.id);
                                setIsModalOpen(true);
                            } finally {
                                setIsCreatingLicense(false);
                            }
                        }}
                        loading={isCreatingLicense}
                        disabled={isCreatingLicense}
                    >
                        {t("sidepanels:groupPanel.enrollmentTab.startEnrollment")}
                    </PrimaryButton>
                </Box>

                <GenerateQRCodeDialogWrapper groupId={props.id} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:groupPanel.enrollmentTab.defaultEnrollmentGroup")}</PaneElement.Header>
                <div className={classes.defaultGroupContainer}>
                    <PaneElement.Text>
                        {t("sidepanels:groupPanel.enrollmentTab.defaultEnrollmentGroupDescription")}
                    </PaneElement.Text>
                    <PrimaryButton
                        onClick={() => setOpen(true)}
                        disabled={isDefaultGroup}
                        tooltipProps={{
                            title: t("sidepanels:groupPanel.tooltip.defaultGroup"),
                            hide: !isDefaultGroup,
                        }}
                    >
                        {t("sidepanels:groupPanel.button.setAsDefault")}
                    </PrimaryButton>
                </div>
            </PaneElement.Section>

            <PaneElement.Section>
                <GenerateLicensesSection groupId={props.id} groupName={props.name} groupStatistics={currentGroup.statistics} />
            </PaneElement.Section>

            <PaneElement.Section withoutBorder>
                <PaneElement.Header>{t("sidepanels:groupPanel.enrollmentTab.universalLicenses")}</PaneElement.Header>
                <UniversalLicensesTable groupId={props.id} licenses={licenses} groups={groups} />
            </PaneElement.Section>

            <ConfirmDialog
                open={open}
                onConfirm={onConfirm}
                onClose={() => setOpen(false)}
                title={t("modals:changeDefaultGroup.title")}
            >
                {defaultGroup ? (
                    <DialogContentText>
                        <Trans
                            ns="modals"
                            i18nKey="changeDefaultGroup.replaceMessage"
                            values={{
                                currentGroupName: currentGroup.name,
                                defaultGroupName: defaultGroup.name,
                            }}
                            components={{
                                bold: <span className={classes.bold} />,
                            }}
                        />
                    </DialogContentText>
                ) : (
                    <DialogContentText>
                        <Trans
                            ns="modals"
                            i18nKey="changeDefaultGroup.setMessage"
                            values={{
                                currentGroupName: currentGroup.name,
                            }}
                            components={{
                                bold: <span className={classes.bold} />,
                            }}
                        />
                    </DialogContentText>
                )}
            </ConfirmDialog>
        </PaneElement.Container>
    );
};

export default withExpiredWrapper(EnrollmentTab);
